import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar } from "components";
import { useGlobalStore, useSendChatMessage } from "controller";
import { useGetChatMembers } from "controller/hooks/chat/useChatMembers";
import { useEditChatMessage } from "controller/hooks/chat/useEditChat";
import {
  threadMessagesKeys,
  useGetThreadMessages,
} from "controller/hooks/chat/useThreadMessages";
import dayjs from "dayjs";
import { useAtomValue, useSetAtom } from "jotai";
import {
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import type {
  AccessSpecifier,
  ChatMetaData,
  GetThreadMessagesResponse,
  ThreadMessage,
} from "shared/types";
import { getFullName } from "utilz/helpers/stringManipulator";
import { ChatInputSection } from "./ChatInputSection";
import { ChatSearchDrawer } from "./ChatSearchDrawer";
import { ChatMembersDrawer } from "./components/ChatMembersDrawer";
import {
  CustomerMessage,
  canEditMessage,
  MemberTimelineBlock,
  MessageFooter,
  ResourceMessage,
} from "./MessageBlocks";
import { SafeHtmlRenderer } from "./SafeHtmlRenderer";
import {
  chatEditingMessageId,
  chatThreadParentMessage,
  chatThreadSendMode,
  clearChatReplyToMessage,
  setChatEditingMessageId,
  setChatThreadSendMode,
  threadEditorFocusRequest,
} from "./store";
import { ThreadHeader } from "./ThreadHeader";
import type { ChatMessageInputRef } from "./TipTapChatInput";
import { stripHtml } from "./utils/stripHtml";

// ─── ThreadParentMessage ─────────────────────────────────────────────

interface ThreadParentMessageProps {
  message: ThreadMessage;
}

const ThreadParentMessage = memo(function ThreadParentMessage({
  message,
}: ThreadParentMessageProps) {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const senderType: "resource" | "customer" =
    message.sender.user_type === "resource" ? "resource" : "customer";

  return (
    <Stack
      sx={{
        p: "12px",
        borderRadius: "8px",
        gap: "8px",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
      }}
    >
      {/* Header: Avatar + Name + dot + Time */}
      <Stack direction="row" alignItems="center" gap="8px" mb="8px">
        <Avatar
          variants="sm"
          fullname={getFullName(
            message.sender.first_name,
            null,
            message.sender.last_name,
          )}
          url={message.sender.profile_pic ?? ""}
          isAgent={message.is_helena_user}
        />
        <Typography variant="caption-2" sx={{ textTransform: "capitalize" }}>
          {getFullName(
            message.sender.first_name,
            null,
            message.sender.last_name,
          )}
        </Typography>
        <Box
          component="span"
          sx={{
            width: 4,
            height: 4,
            borderRadius: "50%",
            bgcolor: theme.palette.lightBg.low,
            flexShrink: 0,
          }}
        />
        <Typography variant="body-small" color={theme.palette.lightBg.low}>
          {dayjs(message.created_at).tz(timezone).format("hh:mmA")}
        </Typography>
      </Stack>

      {/* Message body */}
      <Box
        sx={{
          ...theme.typography["body-medium"],
          color: theme.palette.lightBg.high,
          wordBreak: "break-word",
        }}
      >
        <SafeHtmlRenderer html={message.message} />
      </Box>

      {/* Footer icons */}
      <MessageFooter
        senderType={senderType}
        accessSpecifier={message.access_specifier}
        isEdited={message.editing?.is_edited}
        messagePlatform={message.message_platform}
        sentAsEmail={message.send_as_email}
        type={
          message.message_platform === "email"
            ? "email"
            : ((message.message_type ?? "normal") as
                | "canned_notes"
                | "time_entry_notes"
                | "normal")
        }
      />
    </Stack>
  );
});

// ─── Helper ──────────────────────────────────────────────────────────

function getThreadMessageType(message: ThreadMessage): "resource" | "customer" {
  if (message.sender.user_type !== "resource") return "customer";
  return "resource";
}

// ─── ThreadMessageRow ────────────────────────────────────────────────

interface ThreadMessageRowProps {
  message: ThreadMessage;
  resourceId: string;
  isFirstInGroup: boolean;
  onEditClick?: () => void;
  isEditing?: boolean;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
}

// Noop handler -- thread messages don't support pin/mark-resolved actions
const noop = () => {
  // intentionally empty
};

function ThreadMemberTimeline({
  message,
  resourceId,
}: {
  message: ThreadMessage;
  resourceId: string;
}) {
  return (
    <MemberTimelineBlock
      sender={message.sender}
      timelineMeta={message.timeline_meta}
      resourceId={resourceId}
    />
  );
}

function ThreadChatMessage({
  message,
  isFirstInGroup,
  onEditClick,
  isEditing,
  onEditSave,
  onEditCancel,
}: Omit<ThreadMessageRowProps, "resourceId">) {
  const type = getThreadMessageType(message);

  const messageSource =
    message.message_platform === "email"
      ? ("email" as const)
      : ((message.message_type ?? "normal") as
          | "canned_notes"
          | "time_entry_notes"
          | "normal");

  if (type === "resource") {
    return (
      <ResourceMessage
        type={message.access_specifier}
        isMarkedAsResolved={message.mark_as_resolved ?? false}
        onMarkAsResolvedClick={noop}
        onEditClick={onEditClick}
        message={message.message}
        date={message.created_at}
        attachments={message.attachments}
        senderDisplayName={getFullName(
          message.sender.first_name,
          null,
          message.sender.last_name,
        )}
        imageUrl={message.sender.profile_pic ?? ""}
        accessSpecifier={message.access_specifier}
        isEdited={message.editing?.is_edited}
        messagePlatform={message.message_platform}
        isFirstInGroup={isFirstInGroup}
        isEditing={isEditing}
        onEditSave={onEditSave}
        onEditCancel={onEditCancel}
        chatId={message.chat_id}
        sentAsEmail={message.send_as_email}
        messageSource={messageSource}
      />
    );
  }

  return (
    <CustomerMessage
      type={message.access_specifier}
      isMarkedAsResolved={message.mark_as_resolved ?? false}
      onMarkAsResolvedClick={noop}
      onEditClick={onEditClick}
      senderDisplayName={getFullName(
        message.sender.first_name,
        null,
        message.sender.last_name,
      )}
      imageUrl={message.sender.profile_pic ?? ""}
      message={message.message}
      attachments={message.attachments}
      accessSpecifier={message.access_specifier}
      isEdited={message.editing?.is_edited}
      date={message.created_at}
      messagePlatform={message.message_platform ?? null}
      isFirstInGroup={isFirstInGroup}
      isEditing={isEditing}
      onEditSave={onEditSave}
      onEditCancel={onEditCancel}
      chatId={message.chat_id}
      sentAsEmail={message.send_as_email}
      messageSource={messageSource}
      senderUserType={message.sender.user_type}
      isHelenaUser={message.is_helena_user}
    />
  );
}

const ThreadMessageRow = memo(function ThreadMessageRow({
  message,
  resourceId,
  isFirstInGroup,
  onEditClick,
  isEditing,
  onEditSave,
  onEditCancel,
}: ThreadMessageRowProps) {
  if (message.message_type === "member_timeline") {
    return <ThreadMemberTimeline message={message} resourceId={resourceId} />;
  }

  return (
    <ThreadChatMessage
      message={message}
      isFirstInGroup={isFirstInGroup}
      onEditClick={onEditClick}
      isEditing={isEditing}
      onEditSave={onEditSave}
      onEditCancel={onEditCancel}
    />
  );
});

// ─── Thread List Item Type ───────────────────────────────────────────

type ThreadListItem =
  | { kind: "parent"; message: ThreadMessage; key: string }
  | { kind: "divider"; replyCount: number; key: string }
  | {
      kind: "reply";
      message: ThreadMessage;
      key: string;
      isFirstInGroup: boolean;
    };

const GROUP_TIME_THRESHOLD_MS = 15 * 60000;

function isGroupableReply(
  item: ThreadListItem,
): item is ThreadListItem & { kind: "reply" } {
  return (
    item.kind === "reply" && item.message.message_type !== "member_timeline"
  );
}

/** Find the nearest previous groupable reply before index i. */
function findPrevGroupableReply(
  items: ThreadListItem[],
  i: number,
): (ThreadListItem & { kind: "reply" }) | null {
  for (let j = i - 1; j >= 0; j--) {
    const candidate = items[j];
    if (candidate.kind !== "reply") return null;
    if (isGroupableReply(candidate)) return candidate;
  }
  return null;
}

/** Mark consecutive same-sender replies within 15 min as grouped. */
function applyReplyGrouping(items: ThreadListItem[]): void {
  for (let i = 1; i < items.length; i++) {
    const curr = items[i];
    if (!isGroupableReply(curr)) continue;

    const prev = findPrevGroupableReply(items, i);
    if (!prev) continue;

    const sameSender =
      curr.message.sender.mapping_id === prev.message.sender.mapping_id;
    const timeDiff = Math.abs(
      new Date(curr.message.created_at).getTime() -
        new Date(prev.message.created_at).getTime(),
    );

    const sameThread =
      (curr.message.thread_id ?? null) === (prev.message.thread_id ?? null);

    if (sameSender && sameThread && timeDiff <= GROUP_TIME_THRESHOLD_MS) {
      curr.isFirstInGroup = false;
    }
  }
}

function buildThreadFlatList(
  parentMessage: ThreadMessage | undefined,
  replies: ThreadMessage[],
  replyCount: number,
): ThreadListItem[] {
  const items: ThreadListItem[] = [];

  if (parentMessage) {
    items.push({
      kind: "parent",
      message: parentMessage,
      key: `parent-${parentMessage.chat_id}`,
    });
  }

  items.push({
    kind: "divider",
    replyCount,
    key: "replies-divider",
  });

  for (const reply of replies) {
    items.push({
      kind: "reply",
      message: reply,
      key: reply.chat_id,
      isFirstInGroup: true,
    });
  }

  applyReplyGrouping(items);

  return items;
}

// ─── RepliesDivider ──────────────────────────────────────────────────

const RepliesDivider = memo(function RepliesDivider({
  count,
}: {
  count: number;
}) {
  const theme = useTheme();
  const label = count === 1 ? "1 Reply" : `${count} Replies`;

  return (
    <Box sx={{ px: "24px", py: "8px" }}>
      <Divider
        sx={{
          ...theme.typography["body-small"],
          color: theme.palette.lightBg.low,
        }}
      >
        {label}
      </Divider>
    </Box>
  );
});

// ─── ThreadPanel ─────────────────────────────────────────────────────

interface ThreadPanelProps {
  serviceType: "ticket" | "task";
  serviceId: string;
  serviceSource?: string;
  isDisabled: boolean;
  isTriageInProgress?: boolean;
  metaData?: ChatMetaData;
  onClose: () => void;
}

export const ThreadPanel = memo(function ThreadPanel({
  serviceType,
  serviceId,
  serviceSource,
  isDisabled,
  isTriageInProgress,
  metaData,
  onClose,
}: ThreadPanelProps) {
  const theme = useTheme();
  const resourceId = useGlobalStore((state) => state.resourceId) ?? "";
  const parentMessage = useAtomValue(chatThreadParentMessage);
  const clearReplyAtom = useSetAtom(clearChatReplyToMessage);
  const threadFocusReq = useAtomValue(threadEditorFocusRequest);
  const editorRef = useRef<ChatMessageInputRef>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null) as RefObject<VirtuosoHandle>;
  const queryClient = useQueryClient();

  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  // ─── Edit Message State & Mutation ──────────────────────────────
  const editingMessageId = useAtomValue(chatEditingMessageId);
  const setEditingId = useSetAtom(setChatEditingMessageId);
  const { mutateAsync: editMessage } = useEditChatMessage();

  const threadId = parentMessage?.thread_id ?? null;

  const { data: threadMembersData } = useGetChatMembers(
    serviceId,
    threadId ?? undefined,
  );

  const { data, isLoading } = useGetThreadMessages(threadId);

  const { mutateAsync: sendMessage } = useSendChatMessage();

  // Build the thread access specifier from the thread data or parent message
  const threadAccessSpecifier: AccessSpecifier =
    data?.data?.thread?.access_specifier ??
    parentMessage?.access_specifier ??
    "private";

  // ─── Thread send mode (synced from thread access specifier) ──────
  const threadSendMode = useAtomValue(chatThreadSendMode);
  const setThreadSendMode = useSetAtom(setChatThreadSendMode);

  useEffect(() => {
    setThreadSendMode(threadAccessSpecifier);
  }, [threadAccessSpecifier, setThreadSendMode]);

  // Focus thread editor when a focus request is dispatched
  useEffect(() => {
    if (threadFocusReq === 0) return;
    editorRef.current?.focus();
  }, [threadFocusReq]);

  // Auto-focus thread editor when parent thread changes (thread switch)
  useEffect(() => {
    const tid = parentMessage?.thread_id;
    if (!tid) return;
    const id = setTimeout(() => editorRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [parentMessage?.thread_id]);

  // Build flat list for Virtuoso
  const threadFlatList: ThreadListItem[] = (() => {
    if (!data?.data) return [];
    return buildThreadFlatList(
      data.data.parent_message,
      data.data.messages,
      data.data.thread.reply_count ?? data.data.messages.length,
    );
  })();

  // ─── Thread reply fields (derived from parent) ────────────────────

  const parentMessageId =
    parentMessage?.parent_message_id ?? parentMessage?.chat_id ?? null;
  const parentExcerpt =
    parentMessage?.excerpt ??
    (parentMessage?.message
      ? stripHtml(parentMessage.message).slice(0, 500)
      : null);

  // ─── Send handler ──────────────────────────────────────────────────

  const handleThreadSend = useCallback(
    async ({
      messageText,
      attachmentArray,
      mentionedUsers,
      mentionedEmails,
    }: {
      messageText: string;
      attachmentArray?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
    }) => {
      if (isSendingMessage || isDisabled) return;

      const trimmedValue = messageText.trim();
      const hasContent =
        trimmedValue.length > 0 || Boolean(attachmentArray?.length);
      if (!hasContent) return;

      setIsSendingMessage(true);
      try {
        await sendMessage({
          message: trimmedValue,
          accessSpecifier: threadSendMode,
          serviceId,
          serviceType,
          messageType: "normal",
          ...(threadId && { thread_id: threadId }),
          ...(parentMessageId && { parent_message_id: parentMessageId }),
          ...(parentExcerpt && { excerpt: parentExcerpt }),
          ...(attachmentArray?.length && { attachments: attachmentArray }),
          ...(mentionedUsers?.length && { mentioned_users: mentionedUsers }),
          ...(mentionedEmails?.length && { mentioned_emails: mentionedEmails }),
        });

        clearReplyAtom();
        editorRef.current?.clear();

        queryClient.invalidateQueries({
          queryKey: threadMessagesKeys.detail(threadId ?? ""),
        });
      } catch {
        // Error handled by mutation hook / apiErrorHandler
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      isSendingMessage,
      isDisabled,
      sendMessage,
      threadSendMode,
      serviceId,
      serviceType,
      threadId,
      parentMessageId,
      parentExcerpt,
      clearReplyAtom,
      queryClient,
    ],
  );

  // ─── Edit Handlers ──────────────────────────────────────────────────

  const handleThreadEditSave = useCallback(
    async (chatId: string, newContent: string) => {
      try {
        const result = await editMessage({
          serviceType,
          serviceId,
          chatId,
          message: newContent,
        });
        // Update thread cache directly
        queryClient.setQueryData<GetThreadMessagesResponse>(
          threadMessagesKeys.detail(threadId ?? ""),
          (oldData) => {
            if (!oldData?.data) return oldData;
            return {
              ...oldData,
              data: {
                ...oldData.data,
                messages: oldData.data.messages.map((msg) =>
                  msg.chat_id === chatId
                    ? {
                        ...msg,
                        message: result.message,
                        editing: {
                          is_edited: true as const,
                          edited_at: result.editing.edited_at,
                          edited_by: result.editing.edited_by,
                        },
                      }
                    : msg,
                ),
              },
            };
          },
        );
        setEditingId(null);
      } catch {
        // Error handled by apiErrorHandler
      }
    },
    [editMessage, serviceType, serviceId, threadId, queryClient, setEditingId],
  );

  const handleThreadEditCancel = useCallback(() => {
    setEditingId(null);
  }, [setEditingId]);

  // ─── Virtuoso render item ──────────────────────────────────────────

  const renderItem = useCallback(
    (_index: number, item: ThreadListItem) => {
      if (item.kind === "divider") {
        return <RepliesDivider count={item.replyCount} />;
      }

      if (item.kind === "parent") {
        return (
          <Box sx={{ px: "24px", display: "flex", flexDirection: "column" }}>
            <ThreadParentMessage message={item.message} />
          </Box>
        );
      }

      const editable = canEditMessage(item.message, resourceId);

      return (
        <Box sx={{ px: "24px", display: "flex", flexDirection: "column" }}>
          <ThreadMessageRow
            message={item.message}
            resourceId={resourceId}
            isFirstInGroup={item.isFirstInGroup}
            onEditClick={
              editable ? () => setEditingId(item.message.chat_id) : undefined
            }
            isEditing={editingMessageId === item.message.chat_id}
            onEditSave={handleThreadEditSave}
            onEditCancel={handleThreadEditCancel}
          />
        </Box>
      );
    },
    [
      resourceId,
      editingMessageId,
      setEditingId,
      handleThreadEditSave,
      handleThreadEditCancel,
    ],
  );

  const Footer = useCallback(() => <Box sx={{ pb: "48px" }} />, []);

  // ─── Empty / placeholder state ─────────────────────────────────────

  if (!parentMessage) {
    return (
      <Stack
        sx={{
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Select a thread to view
        </Typography>
      </Stack>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <Stack
      width="100%"
      height="100%"
      direction="column"
      justifyContent="space-between"
    >
      <ThreadHeader
        members={threadMembersData ?? []}
        onMembersClick={() => setMembersDrawerOpen(true)}
        onSearchClick={() => setSearchDrawerOpen(true)}
        onClose={onClose}
      />
      <ChatMembersDrawer
        open={membersDrawerOpen}
        onClose={() => setMembersDrawerOpen(false)}
        onOpen={() => setMembersDrawerOpen(true)}
        serviceId={serviceId}
        threadId={threadId ?? undefined}
        isDisabled={serviceType === "task" || isDisabled}
      />
      {searchDrawerOpen && (
        <ChatSearchDrawer
          open={searchDrawerOpen}
          onClose={() => setSearchDrawerOpen(false)}
          serviceType={serviceType}
          serviceId={serviceId}
          threadId={threadId ?? undefined}
          onResultClick={() => setSearchDrawerOpen(false)}
        />
      )}

      {isLoading ? (
        <Stack
          sx={{
            flexGrow: 1,
            justifyContent: "center",
            alignItems: "center",
            padding: "48px",
          }}
        >
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Virtuoso
          ref={virtuosoRef}
          data={threadFlatList}
          initialTopMostItemIndex={
            threadFlatList.length > 0 ? threadFlatList.length - 1 : 0
          }
          followOutput="smooth"
          alignToBottom
          skipAnimationFrameInResizeObserver
          increaseViewportBy={{ top: 400, bottom: 200 }}
          itemContent={renderItem}
          components={{ Footer }}
          style={{
            flexGrow: 1,
            width: "100%",
          }}
        />
      )}

      <ChatInputSection
        key={serviceId}
        serviceType={serviceType}
        serviceId={serviceId}
        serviceSource={serviceSource}
        isDisabled={isDisabled}
        isTriageInProgress={isTriageInProgress}
        metaData={metaData}
        isSendingMessage={isSendingMessage}
        editorRef={editorRef}
        disabledModeSwitcher={threadAccessSpecifier === "private"}
        sendMode={threadSendMode}
        onModeChange={setThreadSendMode}
        onManageParticipantsClick={() => setMembersDrawerOpen(true)}
        onSend={handleThreadSend}
      />
    </Stack>
  );
});
